import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Tournament } from "../../types";
import { dictionaries } from "../../utils/dictionary";
import { apiService } from "../../services/api";
import {
  PhaseBackground,
  PhaseContainer,
  PhaseMain,
  PhaseButton,
  SectionCard,
} from "../../styles/SharedStyles";
import { theme } from "../../styles/theme";
import PhaseHeader from "../Game/PhaseHeader";

interface TournamentSeatsProps {
  language: number;
  tournament: Tournament;
  onBack: () => void;
  onGameSelect: (
    partNumber: number,
    tableNumber: number,
    sessionNumber: number,
    isFinal: boolean
  ) => void;
}

interface TournamentSeat {
  part_number: number;
  table_number: number;
  session_number: number;
  is_final: boolean;
  completed: boolean;
}

const SectionTitle = styled.h5`
  font-size: 1.125rem;
  font-weight: 700;
  margin: 0 0 ${theme.spacing.md};
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const StageIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  background: rgba(124, 93, 255, 0.15);
  color: rgba(124, 93, 255, 0.9);
  font-size: 0.9rem;
  font-weight: 700;
`;

const TableGroup = styled.div`
  margin-left: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
`;

const TableTitle = styled.h6`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 ${theme.spacing.sm};
  color: ${theme.colors.text.secondary};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};

  &::before {
    content: "↳";
    color: ${theme.colors.text.muted};
  }
`;

const SessionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: ${theme.spacing.sm};
  margin-left: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }
`;

const SessionButton = styled(PhaseButton)`
  padding: 0.75rem ${theme.spacing.md};
  font-size: 0.9rem;
  justify-content: center;
`;

const EmptyState = styled.div`
  padding: ${theme.spacing.xxl};
  text-align: center;
  color: ${theme.colors.text.muted};
  font-size: 1rem;
  line-height: 1.6;

  p {
    margin: 0 0 ${theme.spacing.md};
  }

  strong {
    color: ${theme.colors.text.secondary};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.lg};
  min-height: 40vh;
  color: ${theme.colors.text.secondary};

  p {
    font-size: 1.1rem;
    margin: 0;
  }
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(124, 93, 255, 0.2);
  border-top-color: rgba(124, 93, 255, 0.8);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const TournamentSeats: React.FC<TournamentSeatsProps> = ({
  language,
  tournament,
  onBack,
  onGameSelect,
}) => {
  const [seats, setSeats] = useState<TournamentSeat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadedRef = React.useRef(false);

  const dict = dictionaries[language].ph;

  useEffect(() => {
    // Prevent double loading in React.StrictMode
    if (loadedRef.current) return;
    loadedRef.current = true;
    
    window.scrollTo(0, 0);
    loadSeats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSeats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getTournamentSeats(
        tournament.tournament_id
      );

      if (response.success && Array.isArray(response.success)) {
        setSeats(response.success);
      } else if (response.body && Array.isArray(response.body)) {
        setSeats(response.body);
      } else {
        setSeats([]);
      }
    } catch (err) {
      console.error("Failed to load tournament seats:", err);
      setError(dict.failed_load_seats);
    } finally {
      setLoading(false);
    }
  };

  // Group seats by part and table
  const groupedSeats = seats.reduce((acc, seat) => {
    const partKey = `part_${seat.part_number}`;
    const tableKey = `table_${seat.table_number}`;

    if (!acc[partKey]) {
      acc[partKey] = {};
    }
    if (!acc[partKey][tableKey]) {
      acc[partKey][tableKey] = [];
    }

    acc[partKey][tableKey].push(seat);
    return acc;
  }, {} as Record<string, Record<string, TournamentSeat[]>>);

  // Sort parts and tables
  const sortedParts = Object.keys(groupedSeats).sort((a, b) => {
    const partA = parseInt(a.split("_")[1]);
    const partB = parseInt(b.split("_")[1]);
    return partA - partB;
  });

  if (loading) {
    return (
      <PhaseBackground $variant="setup">
        <PhaseContainer>
          <PhaseHeader
            title={tournament.tournament_name}
            subtitle={dict.loading_seats}
            variant="day"
            language={language}
            onTimeoutToggle={() => {}}
            hideTimer={true}
            actions={[
              {
                id: "back",
                label: dict.back,
                onClick: onBack,
                variant: "secondary",
              },
            ]}
          />
          <PhaseMain>
            <SectionCard>
              <LoadingContainer>
                <Spinner role="status" aria-hidden="true" />
                <p>{dict.loading}...</p>
              </LoadingContainer>
            </SectionCard>
          </PhaseMain>
        </PhaseContainer>
      </PhaseBackground>
    );
  }

  if (error || seats.length === 0) {
    return (
      <PhaseBackground $variant="setup">
        <PhaseContainer>
          <PhaseHeader
            title={tournament.tournament_name}
            subtitle={dict.tournament_seating}
            variant="day"
            language={language}
            onTimeoutToggle={() => {}}
            hideTimer={true}
            actions={[
              {
                id: "back",
                label: dict.back,
                onClick: onBack,
                variant: "secondary",
              },
            ]}
          />
          <PhaseMain>
            <SectionCard>
              <EmptyState>
                <p>
                  <strong>{dict.noseats || dict.no_seats_available}</strong>
                </p>
                <p>{dict.seats_not_generated}</p>
                <PhaseButton
                  $variant="outline"
                  onClick={loadSeats}
                  style={{ marginTop: theme.spacing.lg }}
                >
                  ⟳ {dict.refresh}
                </PhaseButton>
              </EmptyState>
            </SectionCard>
          </PhaseMain>
        </PhaseContainer>
      </PhaseBackground>
    );
  }

  return (
    <PhaseBackground $variant="setup">
      <PhaseContainer>
        <PhaseHeader
          title={tournament.tournament_name}
          subtitle={dict.select_session}
          variant="day"
          language={language}
          onTimeoutToggle={() => {}}
          hideTimer={true}
          actions={[
            {
              id: "back",
              label: dict.back,
              onClick: onBack,
              variant: "secondary",
            },
          ]}
        />

        <PhaseMain>
          {sortedParts.map((partKey) => {
            const partNumber = parseInt(partKey.split("_")[1]);
            const tables = groupedSeats[partKey];
            const sortedTables = Object.keys(tables).sort((a, b) => {
              const tableA = parseInt(a.split("_")[1]);
              const tableB = parseInt(b.split("_")[1]);
              return tableA - tableB;
            });

            return (
              <SectionCard
                key={partKey}
                style={{ marginBottom: theme.spacing.xl }}
              >
                <SectionTitle>
                  <StageIcon>{partNumber}</StageIcon>
                  {dict.stage} {partNumber}
                </SectionTitle>

                {sortedTables.map((tableKey) => {
                  const tableNumber = parseInt(tableKey.split("_")[1]);
                  const sessions = tables[tableKey].sort(
                    (a, b) => a.session_number - b.session_number
                  );

                  return (
                    <TableGroup key={tableKey}>
                      <TableTitle>
                        {dict.table} {tableNumber}
                      </TableTitle>
                      <SessionGrid>
                        {sessions.map((seat) => (
                          <SessionButton
                            key={`${seat.part_number}-${seat.table_number}-${seat.session_number}`}
                            $variant="secondary"
                            onClick={() =>
                              onGameSelect(
                                seat.part_number,
                                seat.table_number,
                                seat.session_number,
                                seat.is_final
                              )
                            }
                          >
                            {dict.session} {seat.session_number}
                            {seat.is_final ? " 🏆" : ""}
                            {seat.completed ? " ✅" : ""}
                          </SessionButton>
                        ))}
                      </SessionGrid>
                    </TableGroup>
                  );
                })}
              </SectionCard>
            );
          })}
        </PhaseMain>
      </PhaseContainer>
    </PhaseBackground>
  );
};

export default TournamentSeats;
